// frontend/src/components/MyBetsTab.jsx
import { useState, useEffect } from 'react';
import { RefreshCw, History, ChevronDown, ChevronRight } from 'lucide-react';
import { colors, fontFamily } from '../theme';
import { formatBetSelection } from '../utils/betFormatting';

const CC = ({ size = '1em' }) => <span style={{ fontSize: size, marginLeft: 4 }}>₵₵</span>;

function calculatePotentialWin(amount, odds) {
  const match = String(odds).match(/^([+-]?\d+)$/);
  if (!match) return 0;
  const n = parseInt(match[1], 10);
  return n > 0 ? amount * (n / 100) : amount * (100 / Math.abs(n));
}

function getStatusBadge(status) {
  const map = {
    pending: { bg: colors.pending, label: 'Pending' },
    won: { bg: colors.odds, label: 'Won' },
    lost: { bg: colors.negative, label: 'Lost' },
    push: { bg: colors.textFaint, label: 'Push' },
    void: { bg: colors.textFaint, label: 'Void' },
    cashed_out: { bg: colors.gold, label: 'Cashed Out' },
  };
  return map[status] || map.pending;
}

// Mirrors the backend cashout allowlist (cashout/engine.py is_cashout_eligible_market):
// h2h / h2h_3way / spreads / totals and their period variants only — no props or
// futures. A legacy row with no market_type is priced as h2h by the backend.
const CASHOUT_MARKET_RE = /^(h2h|spreads|totals)(_[a-z0-9]+)?$/;
function isCashoutEligibleMarket(marketType) {
  return !marketType || CASHOUT_MARKET_RE.test(marketType);
}

// Settled payout color reflects the actual outcome instead of always being
// green — a $0 payout on a lost bet previously looked identical to a win.
function payoutColor(status) {
  if (status === 'lost') return colors.negative;
  if (status === 'push') return colors.textFaint;
  return colors.odds; // won, or pending 'to win' projection
}

export default function MyBetsTab({
  weeklyBalance,
  pendingTotal,
  betHistoryTab,
  onChangeBetHistoryTab,
  betHistoryPeriod,
  onChangePeriod,
  pendingBets,
  pendingParlays,
  pendingLiveBets = [],
  pendingLiveParlays = [],
  pastBets,
  pastParlays,
  loading,
  user,
  onCashout,
  onCashoutBet,
  onCashoutLiveBet,
  onCashoutLiveParlay,
}) {
  const [expandedParlays, setExpandedParlays] = useState({});

  useEffect(() => {
    if (betHistoryTab === 'past' && pastBets.length === 0 && pastParlays.length === 0) {
      onChangePeriod(betHistoryPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betHistoryTab]);

  const toggleParlay = (id) => setExpandedParlays(prev => ({ ...prev, [id]: !prev[id] }));

  const betsToShow = betHistoryTab === 'pending' ? pendingBets : pastBets;
  const parlaysToShow = betHistoryTab === 'pending' ? pendingParlays : pastParlays;

  return (
    <div className="px-3 pb-24" style={{ fontFamily }}>
      {/* Stat chips */}
      <div className="grid grid-cols-2 gap-2 py-3">
        <div className="rounded-lg px-3 py-2" style={{ background: colors.sectionBg }}>
          <p className="text-[11px]" style={{ color: colors.textMuted }}>Weekly</p>
          <p className="text-lg font-bold" style={{ color: weeklyBalance >= 0 ? colors.odds : colors.negative }}>
            {weeklyBalance >= 0 ? '+' : '-'}{Math.abs(weeklyBalance).toFixed(2)}<CC />
          </p>
        </div>
        <div className="rounded-lg px-3 py-2" style={{ background: colors.sectionBg }}>
          <p className="text-[11px]" style={{ color: colors.textMuted }}>Pending</p>
          <p className="text-lg font-bold" style={{ color: colors.pending }}>{pendingTotal.toFixed(2)}<CC /></p>
        </div>
      </div>

      {/* Pending / Past sub-tabs */}
      <div className="flex gap-2 mb-2">
        {['pending', 'past'].map(tab => (
          <button
            key={tab}
            onClick={() => onChangeBetHistoryTab(tab)}
            className="flex-1 py-2 rounded-lg text-sm font-medium capitalize"
            style={{ background: betHistoryTab === tab ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
          >
            {tab}
          </button>
        ))}
      </div>

      {betHistoryTab === 'past' && (
        <div className="flex gap-1.5 mb-2">
          {['week', 'month', 'all'].map(period => (
            <button
              key={period}
              onClick={() => onChangePeriod(period)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium capitalize"
              style={{ background: betHistoryPeriod === period ? colors.goldActiveBg : colors.sectionBg, color: colors.textPrimary }}
            >
              {period === 'all' ? 'All-Time' : period}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><RefreshCw className="w-5 h-5 animate-spin mx-auto" style={{ color: colors.textFaint }} /></div>
      ) : betsToShow.length === 0 && parlaysToShow.length === 0 &&
          (betHistoryTab !== 'pending' || (pendingLiveBets.length === 0 && pendingLiveParlays.length === 0)) ? (
        <div className="text-center py-16" style={{ color: colors.textFaint }}>
          <History className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">No bets here yet</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {betsToShow.map(bet => {
            const badge = getStatusBadge(bet.status);
            const toWin = bet.status === 'pending' ? calculatePotentialWin(bet.amount, bet.odds) : null;
            return (
              <div key={bet.id} className="rounded-lg p-3" style={{ background: colors.cardBg, boxShadow: colors.shadowSubtle }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: badge.bg, color: '#0a040b' }}>{badge.label}</span>
                  {bet.is_free_play && <span className="text-[10px]" style={{ color: colors.freePlay }}>FREE PLAY</span>}
                </div>
                <p className="text-[15px] font-semibold" style={{ color: colors.textPrimary }}>
                  {bet.player_name || (bet.market_type && (bet.market_type.startsWith('team_total_') || bet.market_type.startsWith('totals')))
                    // player_name/market_type/point/home_team/away_team are real columns
                    // (reliable even on old rows), unlike bet.description/selection which
                    // just echo whatever text was generated when the bet was placed — so
                    // this also cleans up old bets placed before the format above existed.
                    ? `${bet.away_team && bet.home_team ? `${bet.away_team} @ ${bet.home_team} - ` : ''}${formatBetSelection(bet)}`
                    : (bet.description || bet.selection)}
                  {bet.bet_type === 'casino' && bet.round_count > 1 && (
                    <span className="font-normal" style={{ color: colors.textPrimary }}> • {bet.round_count} rounds</span>
                  )}
                </p>
                <div className="flex items-center justify-between mt-1 text-sm" style={{ color: colors.textMuted }}>
                  <span>Risk: {bet.amount.toFixed(2)}<CC /></span>
                  {bet.bet_type !== 'casino' && (
                    <span>Odds: <span style={{ color: colors.odds }}>{bet.odds}</span></span>
                  )}
                  <span>{bet.status === 'pending' ? 'To Win' : 'Payout'}: <span style={{ color: payoutColor(bet.status) }}>{(bet.status === 'pending' ? toWin : (bet.payout || 0)).toFixed(2)}<CC /></span></span>
                </div>
                {bet.status === 'pending' && user.cashout_enabled && isCashoutEligibleMarket(bet.market_type) && (
                  <button
                    onClick={() => onCashoutBet(bet)}
                    className="mt-2 w-full py-1.5 rounded-lg text-[14px] font-medium"
                    style={{ background: colors.sectionBg, color: colors.textPrimary }}
                  >
                    Cash Out
                  </button>
                )}
              </div>
            );
          })}

          {parlaysToShow.map(parlay => {
            const badge = getStatusBadge(parlay.status);
            const isExpanded = expandedParlays[parlay.id];

            const displayPayout = (() => {
              if (parlay.status === 'pending') {
                const potPayout = parlay.potential_payout || 0;
                const maxPayout = user.parlay_max_payout;
                return (maxPayout && maxPayout > 0) ? Math.min(potPayout, maxPayout) : potPayout;
              }
              if (parlay.status === 'cashed_out') return parlay.cashout_amount || 0;
              if (parlay.status === 'won') return parlay.payout || 0;
              return 0; // lost, void
            })();

            return (
              <div key={parlay.id} className="rounded-lg p-3" style={{ background: colors.cardBg, boxShadow: colors.shadowSubtle }}>
                <div className="mb-1">
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: badge.bg, color: '#0a040b' }}>{badge.label}</span>
                </div>
                <button onClick={() => toggleParlay(parlay.id)} className="w-full flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                    <span className="text-[15px] font-semibold truncate" style={{ color: colors.textPrimary }}>{parlay.legs?.length || 0}-Leg Parlay</span>
                  </div>
                  <span className="text-[15px]" style={{ color: colors.textMuted }}>Risk: {parlay.amount.toFixed(2)}<CC /></span>
                  <span className="text-[15px] flex-shrink-0" style={{ color: colors.textMuted }}>
                    {parlay.status === 'pending' ? 'To Win' : 'Payout'}: <span style={{ color: colors.odds }}>{displayPayout.toFixed(2)}<CC /></span>
                  </span>
                </button>
                {isExpanded && (
                  <div className="mt-2 flex flex-col gap-1.5 pl-6">
                    {(parlay.legs || []).map((leg, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs" style={{ color: colors.textMuted }}>
                        {/* Short form here — no away@home team-name prefix — since the
                            expanded legs list gets long/cramped; the full matchup is
                            still shown everywhere else (bet slip, pending/past bets). */}
                        <span>{leg.status === 'won' ? '✓' : leg.status === 'lost' ? '✗' : '○'} {formatBetSelection(leg)}</span>
                        <span>Odds: <span style={{ color: colors.odds }}>{leg.odds}</span></span>
                      </div>
                    ))}
                  </div>
                )}
                {parlay.status === 'pending' && user.cashout_enabled && (
                  <button
                    onClick={() => onCashout(parlay)}
                    className="mt-2 w-full py-1.5 rounded-lg text-[14px] font-medium"
                    style={{ background: colors.sectionBg, color: colors.textPrimary }}
                  >
                    Cash Out
                  </button>
                )}
              </div>
            );
          })}

          {betHistoryTab === 'pending' && (
            <>
              {pendingLiveBets.map(bet => (
                <div key={`live-${bet.id}`} className="rounded-lg p-3" style={{ background: colors.cardBg, boxShadow: colors.shadowSubtle }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: colors.goldActiveBg, color: '#0a040b' }}>LIVE</span>
                    {bet.is_free_play && <span className="text-[10px]" style={{ color: colors.freePlay }}>FREE PLAY</span>}
                  </div>
                  <p className="text-[15px] font-semibold" style={{ color: colors.textPrimary }}>
                    {bet.away_team} @ {bet.home_team} - {bet.selection}
                  </p>
                  <div className="flex items-center justify-between mt-1 text-sm" style={{ color: colors.textMuted }}>
                    <span>Risk: {bet.amount.toFixed(2)}<CC /></span>
                    <span>Odds: <span style={{ color: colors.odds }}>{bet.odds_american > 0 ? '+' : ''}{bet.odds_american}</span></span>
                    <span>To Win: <span style={{ color: colors.odds }}>{bet.potential_payout.toFixed(2)}<CC /></span></span>
                  </div>
                  {user.cashout_enabled && (
                    <button
                      onClick={() => onCashoutLiveBet(bet)}
                      className="mt-2 w-full py-1.5 rounded-lg text-[14px] font-medium"
                      style={{ background: colors.sectionBg, color: colors.textPrimary }}
                    >
                      Cash Out
                    </button>
                  )}
                </div>
              ))}

              {pendingLiveParlays.map(parlay => (
                <div key={`live-parlay-${parlay.id}`} className="rounded-lg p-3" style={{ background: colors.cardBg, boxShadow: colors.shadowSubtle }}>
                  <div className="mb-1">
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: colors.goldActiveBg, color: '#0a040b' }}>LIVE</span>
                  </div>
                  <p className="text-[15px] font-semibold" style={{ color: colors.textPrimary }}>{parlay.num_legs}-Leg Live Parlay</p>
                  <div className="flex items-center justify-between mt-1 text-sm" style={{ color: colors.textMuted }}>
                    <span>Risk: {parlay.amount.toFixed(2)}<CC /></span>
                    <span>To Win: <span style={{ color: colors.odds }}>{parlay.potential_payout.toFixed(2)}<CC /></span></span>
                  </div>
                  {user.cashout_enabled && (
                    <button
                      onClick={() => onCashoutLiveParlay(parlay)}
                      className="mt-2 w-full py-1.5 rounded-lg text-[14px] font-medium"
                      style={{ background: colors.sectionBg, color: colors.textPrimary }}
                    >
                      Cash Out
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
